import { ACHIEVEMENT_DEFS, type AchievementCounterKey } from '../game/achievements'
import type { GameMode } from '../ecs/world'
import type { LifetimeStats, ScoreEntry, Settings } from '../persist/db'
import { ARCHIVE_FRAGMENTS } from '../story/archive'

/** per-run stats shown on the end screens */
export interface RunStats {
  kills: number
  foodEaten: number
  /** seconds of simulated play */
  runTime: number
  deathCause: string
}

/** the four requisition tracks (rank/price state lives in Game; copy lives here) */
export type RequisitionId = 'cycling' | 'munitions' | 'chassis' | 'authorization'

export interface RequisitionTrack {
  id: RequisitionId
  rank: number
  maxRanks: number
  /** index cost of the next rank; null when fully provisioned */
  price: number | null
}

export interface AmenityOffer {
  id: string
  name: string
  desc: string
  price: number
  owned: boolean
}

export interface RequisitionOffer {
  score: number
  /** index spent on requisitions so far this run (display only) */
  spent: number
  nextFloor: number
  /** one approval per interval — set after a purchase this visit */
  bought: boolean
  /** the Provisioner's line for this visit (fixed per visit — see Game.openRequisition) */
  provisioner: string
  tracks: RequisitionTrack[]
  /** run-scoped perk shelf (same one-approval rule as the tracks) */
  amenities: AmenityOffer[]
  /** persistent meta currency banked at run end */
  residuals: number
  /** next permanent stock unlock, or null when the pool is exhausted */
  nextUnlock: { name: string; cost: number } | null
}

const REQUISITION_COPY: Record<RequisitionId, { name: string; desc: string }> = {
  cycling: { name: 'CYCLING PERMIT', desc: 'WEAPON CYCLING INTERVAL REDUCED 12% PER RANK' },
  munitions: { name: 'AMPLIFIED MUNITIONS', desc: 'PROJECTILE YIELD RAISED 20% PER RANK' },
  chassis: { name: 'REINFORCED CHASSIS', desc: 'VITALS CAPACITY RAISED 20 PER RANK · TRIAGE INCLUDED AT INSTALLATION' },
  authorization: { name: 'EXPANDED AUTHORIZATION', desc: 'CHARGE CAPACITY RAISED 1 PER RANK · ONE CHARGE ISSUED ON APPROVAL' }
}

export interface MenuCallbacks {
  startCampaign: () => void
  startEndless: () => void
  startDaily: () => void
  /** RE-RUN from the records screen: replay a recorded seed from floor 1 */
  startSeeded: (mode: GameMode, seed: number) => void
  resume: () => void
  quitToMenu: () => void
  /** win screen: continue the SAME run into overtime floors 16+ (score kept) */
  continueOvertime: () => void
  /** win screen: end the run victorious at the summit */
  concludeWin: () => void
  settingsChanged: (s: Settings) => void
  requestScores: () => Promise<ScoreEntry[]>
  requestLifetime: () => LifetimeStats
  requestAchievements: () => { earned: ReadonlySet<string>; counters: Record<AchievementCounterKey, number> }
  /** archive screen + records: owned record numbers, quiet-ending flag */
  requestArchive: () => { owned: ReadonlySet<number>; quietEndingSeen: boolean }
  uiSound: (kind: 'move' | 'select') => void
}

/** short display form of a world seed (uppercase hex, fixed width) */
/** already installed to the home screen? (standalone display mode, or iOS's navigator.standalone) */
function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  )
}

/**
 * End-screen incident-report block (issue #38). Rendered only when a live
 * report was actually requested; fills in via presentIncidentReport, or
 * collapses quietly on null — a missing report is never an error state.
 */
function incidentBlock(pending: boolean): string {
  return pending ? '<div class="incident"><span class="dim">INCIDENT REPORT</span><div class="incident-body dim">REPORT PENDING…</div></div>' : ''
}

function seedLabel(seed: number): string {
  return (seed >>> 0).toString(16).toUpperCase().padStart(8, '0')
}

/**
 * DOM menu shell (no framework — the menu complexity doesn't earn React).
 * All screens are keyboard-navigable with visible focus.
 */
export class Menus {
  private root: HTMLElement
  private escHandler: ((e: KeyboardEvent) => void) | null = null

  constructor(
    private cb: MenuCallbacks,
    private getSettings: () => Settings
  ) {
    this.root = document.getElementById('menu-root')!
  }

  hide(): void {
    this.setEscAction(null)
    document.body.classList.remove('req-open')
    this.root.innerHTML = ''
    this.root.classList.remove('active')
  }

  /** bind (or clear) what Escape does while a screen is up */
  private setEscAction(action: (() => void) | null): void {
    if (this.escHandler) {
      window.removeEventListener('keydown', this.escHandler)
      this.escHandler = null
    }
    if (action) {
      this.escHandler = (e: KeyboardEvent) => {
        if (e.code === 'Escape') action()
      }
      window.addEventListener('keydown', this.escHandler)
    }
  }

  private screen(cls: string, html: string): HTMLElement {
    this.setEscAction(null)
    document.body.classList.remove('req-open')
    this.root.innerHTML = `<div class="screen ${cls}">${html}</div>`
    this.root.classList.add('active')
    const el = this.root.querySelector('.screen') as HTMLElement
    const first = el.querySelector<HTMLElement>('button, input, select')
    first?.focus()
    return el
  }

  private wireButtons(el: HTMLElement): void {
    el.querySelectorAll('button').forEach((b) => {
      b.addEventListener('mouseenter', () => this.cb.uiSound('move'))
      b.addEventListener('focus', () => this.cb.uiSound('move'))
    })
  }

  showMain(endlessUnlocked: boolean, topScore: number, controlsHint: string): void {
    const el = this.screen(
      'main-menu',
      `
      <div class="titleblock">
        <div class="pretitle">REACTIVE UNDERWORLD INTERVENTION NETWORK</div>
        <h1 class="title">RUIN</h1>
        <div class="subtitle">A LICENSED SURVIVAL BROADCAST · PARTICIPATION IS ONGOING</div>
      </div>
      <nav class="menu-nav">
        <button data-a="campaign">BEGIN DESCENT</button>
        <button data-a="endless" ${endlessUnlocked ? '' : 'disabled'}>${endlessUnlocked ? 'OVERTIME (ENDLESS)' : 'OVERTIME — SEALED'}</button>
        <button data-a="daily">DAILY BROADCAST</button>
        <button data-a="scores">PERFORMANCE RECORDS</button>
        <button data-a="record">SERVICE RECORD</button>
        <button data-a="archive">THE ARCHIVE</button>
        <button data-a="settings">COMPLIANCE SETTINGS</button>
        <button data-a="share">SHARE THE BROADCAST</button>
        <button data-a="contact">CONTACT · info@mandorum.com</button>
      </nav>
      <div class="menu-foot">
        <span>BEST INDEX ${String(topScore).padStart(7, '0')}</span>
        <span class="dim">${controlsHint}</span>
        ${endlessUnlocked ? '' : '<span class="dim">complete the scheduled programming to unseal overtime</span>'}
        ${isStandalone() ? '' : '<span class="dim">the broadcast performs best installed to the home screen</span>'}
        <span class="dim share-note" aria-live="polite"></span>
        <span class="dim">REVISION ${__RUIN_VERSION__} · BUILD ${__RUIN_BUILD__}</span>
        <a class="sponsor" href="https://buymeacoffee.com/eddyout" target="_blank" rel="noopener noreferrer">☕ SPONSOR THE BROADCAST — buymeacoffee.com/eddyout</a>
      </div>`
    )
    this.wireButtons(el)
    el.querySelector('[data-a="share"]')!.addEventListener('click', () => {
      this.cb.uiSound('select')
      void this.shareBroadcast(el)
    })
    el.querySelector('[data-a="contact"]')!.addEventListener('click', () => {
      this.cb.uiSound('select')
      // mail draft to the operator; the address stays visible in the label for
      // players without a configured mail client
      location.href = 'mailto:info@mandorum.com?subject=RUIN'
    })
    el.querySelector('[data-a="campaign"]')!.addEventListener('click', () => {
      this.cb.uiSound('select')
      this.cb.startCampaign()
    })
    el.querySelector('[data-a="endless"]')!.addEventListener('click', () => {
      this.cb.uiSound('select')
      if (endlessUnlocked) this.cb.startEndless()
    })
    el.querySelector('[data-a="daily"]')!.addEventListener('click', () => {
      this.cb.uiSound('select')
      this.cb.startDaily()
    })
    el.querySelector('[data-a="scores"]')!.addEventListener('click', () => {
      this.cb.uiSound('select')
      void this.showScores()
    })
    el.querySelector('[data-a="record"]')!.addEventListener('click', () => {
      this.cb.uiSound('select')
      this.showServiceRecord()
    })
    el.querySelector('[data-a="archive"]')!.addEventListener('click', () => {
      this.cb.uiSound('select')
      this.showArchive()
    })
    el.querySelector('[data-a="settings"]')!.addEventListener('click', () => {
      this.cb.uiSound('select')
      this.showSettings(() => this.showMain(endlessUnlocked, topScore, controlsHint))
    })
  }

  /**
   * Share the canonical origin URL (never the current path — dev params like
   * ?floor= must not leak): native share sheet where available (the installed
   * standalone PWA has no browser chrome, so this is the only share path
   * there), clipboard fallback elsewhere.
   */
  private async shareBroadcast(el: HTMLElement): Promise<void> {
    const url = location.origin
    const note = el.querySelector<HTMLElement>('.share-note')
    const confirm = (text: string) => {
      if (!note) return
      note.textContent = text
      window.setTimeout(() => {
        if (note.textContent === text) note.textContent = ''
      }, 4000)
    }
    if (typeof navigator.share === 'function') {
      // an aborted share sheet is a user choice, not an error
      await navigator
        .share({ title: 'RUIN', text: 'RUIN — a licensed survival broadcast. Participation is ongoing.', url })
        .catch(() => undefined)
      return
    }
    try {
      await navigator.clipboard.writeText(url)
      confirm('distribution copied. recruitment is appreciated.')
    } catch {
      confirm(`distribution available at ${url}`)
    }
  }

  showSettings(back: () => void): void {
    const s = this.getSettings()
    const el = this.screen(
      'settings',
      `
      <h2>COMPLIANCE SETTINGS</h2>
      <div class="settings-grid">
        <label>AIM MODE
          <select data-k="classicAim">
            <option value="false" ${s.classicAim ? '' : 'selected'}>MODERN — AIM DECOUPLED FROM MOVEMENT</option>
            <option value="true" ${s.classicAim ? 'selected' : ''}>CLASSIC — SHOOT WHERE YOU WALK</option>
          </select>
        </label>
        <label>INPUT SOURCE
          <select data-k="inputOverride">
            <option value="auto" ${s.inputOverride === 'auto' ? 'selected' : ''}>AUTO-DETECT</option>
            <option value="desktop" ${s.inputOverride === 'desktop' ? 'selected' : ''}>KEYBOARD + MOUSE</option>
            <option value="touch" ${s.inputOverride === 'touch' ? 'selected' : ''}>TOUCH STICKS</option>
          </select>
        </label>
        <label>SFX VOLUME <input type="range" min="0" max="1" step="0.05" value="${s.sfxVolume}" data-k="sfxVolume"></label>
        <label>MUSIC VOLUME <input type="range" min="0" max="1" step="0.05" value="${s.musicVolume}" data-k="musicVolume"></label>
        <label>SCREEN SHAKE
          <select data-k="screenShake">
            <option value="true" ${s.screenShake ? 'selected' : ''}>ENABLED</option>
            <option value="false" ${s.screenShake ? '' : 'selected'}>DISABLED</option>
          </select>
        </label>
        <label>VIBRATION
          <select data-k="haptics">
            <option value="true" ${s.haptics ? 'selected' : ''}>ENABLED</option>
            <option value="false" ${s.haptics ? '' : 'selected'}>DISABLED</option>
          </select>
        </label>
        <label>REDUCED MOTION
          <select data-k="reducedMotion">
            <option value="auto" ${s.reducedMotion === 'auto' ? 'selected' : ''}>FOLLOW SYSTEM</option>
            <option value="on" ${s.reducedMotion === 'on' ? 'selected' : ''}>ON</option>
            <option value="off" ${s.reducedMotion === 'off' ? 'selected' : ''}>OFF</option>
          </select>
        </label>
        <label>LIVE NETWORK COMMENTARY
          <select data-k="liveCommentary">
            <option value="true" ${s.liveCommentary ? 'selected' : ''}>ENABLED — REQUIRES CONNECTION</option>
            <option value="false" ${s.liveCommentary ? '' : 'selected'}>DISABLED — ARCHIVED LINES ONLY</option>
          </select>
        </label>
      </div>
      <button data-a="back">RETURN</button>`
    )
    this.wireButtons(el)
    const apply = () => {
      const next: Settings = { ...s }
      el.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-k]').forEach((inp) => {
        const k = inp.dataset.k as keyof Settings
        const v = inp.value
        if (k === 'sfxVolume' || k === 'musicVolume') (next[k] as number) = parseFloat(v)
        else if (k === 'classicAim' || k === 'screenShake' || k === 'haptics' || k === 'liveCommentary') (next[k] as boolean) = v === 'true'
        else (next[k] as string) = v
      })
      this.cb.settingsChanged(next)
    }
    el.querySelectorAll('[data-k]').forEach((inp) => inp.addEventListener('change', apply))
    el.querySelector('[data-a="back"]')!.addEventListener('click', () => {
      this.cb.uiSound('select')
      back()
    })
  }

  /**
   * Fill (or quietly collapse) the incident-report block on whichever end
   * screen is showing. Display-only: the text is never persisted anywhere.
   * A null report, or the player having already left the screen, is a no-op
   * by design — there is no error state.
   */
  presentIncidentReport(text: string | null): void {
    const block = this.root.querySelector<HTMLElement>('.incident')
    if (!block) return
    if (text === null) {
      block.remove()
      return
    }
    const body = block.querySelector<HTMLElement>('.incident-body')
    if (body) body.textContent = text
  }

  private scoreRows(entries: ScoreEntry[]): string {
    if (entries.length === 0) return '<div class="dim">NO RECORDS. THE ARCHIVE AWAITS.</div>'
    return entries
      .map((e, i) => {
        const tag = `${e.daily ? `DAILY ${e.daily}` : e.mode === 'endless' ? 'OVERTIME' : 'CAMPAIGN'}${e.won ? ' · COMPLETE' : ''}`
        const rerun =
          e.seed !== undefined
            ? `<button class="mini" data-seed="${e.seed}" data-mode="${e.mode}">RE-RUN</button>`
            : '<span class="dim">—</span>'
        return `<div class="score-row"><span>${String(i + 1).padStart(2, '0')}</span><span>${String(e.score).padStart(7, '0')}</span><span>FLOOR ${e.floor}</span><span class="dim">${e.seed !== undefined ? seedLabel(e.seed) : 'NO SEED'}</span><span>${tag}</span>${rerun}</div>`
      })
      .join('')
  }

  private dossierRows(l: LifetimeStats): string {
    const n = (v: number) => Math.floor(v).toLocaleString('en-US')
    const t = Math.max(0, Math.round(l.timeOnAir))
    const clock = `${Math.floor(t / 3600)}:${String(Math.floor((t % 3600) / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`
    return `
      <div>INTAKES PROCESSED <b>${n(l.runs)}</b></div>
      <div>ASSETS PROCESSED (LIFETIME) <b>${n(l.kills)}</b></div>
      <div>RATIONS CONSUMED (LIFETIME) <b>${n(l.foodEaten)}</b></div>
      <div>TIME ON AIR (LIFETIME) <b>${clock}</b></div>
      <div>DEEPEST FLOOR REACHED <b>${l.deepestFloor > 0 ? `FLOOR ${l.deepestFloor}` : 'NONE ON FILE'}</b></div>
      <div>EXPIRATIONS PROCESSED <b>${n(l.deaths)}</b></div>
      <div>FULL DESCENTS CERTIFIED <b>${n(l.campaignWins)}</b></div>`
  }

  async showScores(): Promise<void> {
    const entries = await this.cb.requestScores()
    const lifetime = this.cb.requestLifetime()
    const archive = this.cb.requestArchive()
    const campaign = entries.filter((e) => e.mode === 'campaign').slice(0, 10)
    const overtime = entries.filter((e) => e.mode === 'endless').slice(0, 10)
    const el = this.screen(
      'scores',
      `<h2>PERFORMANCE RECORDS</h2>
      <div class="records-section">
        <h3>CAMPAIGN</h3>
        <div class="score-table">${this.scoreRows(campaign)}</div>
      </div>
      <div class="records-section">
        <h3>OVERTIME</h3>
        <div class="score-table">${this.scoreRows(overtime)}</div>
      </div>
      <div class="records-section">
        <h3>PARTICIPANT DOSSIER</h3>
        <div class="dossier">${this.dossierRows(lifetime)}
          <div>ARCHIVE RECORDS RECOVERED <b>${archive.owned.size} / ${ARCHIVE_FRAGMENTS.length}</b></div>
          <div>QUIET SIGN-OFF <b>${archive.quietEndingSeen ? 'ON FILE' : 'NOT ON FILE'}</b></div>
        </div>
      </div>
      <button data-a="back">RETURN</button>`
    )
    this.wireButtons(el)
    el.querySelectorAll<HTMLButtonElement>('button[data-seed]').forEach((b) => {
      b.addEventListener('click', () => {
        this.cb.uiSound('select')
        this.cb.startSeeded(b.dataset.mode === 'endless' ? 'endless' : 'campaign', Number(b.dataset.seed))
      })
    })
    el.querySelector('[data-a="back"]')!.addEventListener('click', () => {
      this.cb.uiSound('select')
      this.cb.quitToMenu()
    })
  }

  showServiceRecord(): void {
    const { earned, counters } = this.cb.requestAchievements()
    const done = ACHIEVEMENT_DEFS.filter((d) => earned.has(d.id)).length
    const rows = ACHIEVEMENT_DEFS.map((d) => {
      if (earned.has(d.id)) {
        return `<div class="ach earned"><span class="ach-name">${d.name}</span><span class="ach-desc">${d.description}</span></div>`
      }
      const progress =
        d.counter && d.goal
          ? `<span class="ach-prog">PROGRESS ${Math.min(counters[d.counter], d.goal)} / ${d.goal}</span>`
          : ''
      return `<div class="ach sealed"><span class="ach-name">${d.name}</span><span class="ach-desc dim">RECORD SEALED PENDING COMPLIANCE</span>${progress}</div>`
    }).join('')
    const el = this.screen(
      'record',
      `<h2>SERVICE RECORD</h2>
      <div class="record-count dim">${done} / ${ACHIEVEMENT_DEFS.length} ENTRIES PROCESSED</div>
      <div class="ach-list">${rows}</div>
      <button data-a="back">RETURN</button>`
    )
    this.wireButtons(el)
    el.querySelector('[data-a="back"]')!.addEventListener('click', () => {
      this.cb.uiSound('select')
      this.cb.quitToMenu()
    })
  }

  /**
   * THE ARCHIVE: 18 record slots. Owned records read in full (title + body);
   * unowned ones show as withheld rows. Recovery order is enforced by filing
   * (sequential record assignment), so the list always reads top-down.
   */
  showArchive(): void {
    const { owned, quietEndingSeen } = this.cb.requestArchive()
    const rows = ARCHIVE_FRAGMENTS.map((f) => {
      const num = `RECORD ${String(f.n).padStart(2, '0')}`
      if (owned.has(f.n)) {
        return `<div class="frag owned">
          <div class="frag-head"><span class="frag-num">${num}</span><span class="frag-title">${f.title}</span></div>
          <div class="frag-body">${f.body}</div>
        </div>`
      }
      return `<div class="frag sealed">
        <div class="frag-head"><span class="frag-num dim">${num}</span><span class="frag-title dim">RECORD WITHHELD</span></div>
        <div class="frag-body dim">CONTENT WITHHELD PENDING RECOVERY OF THE PHYSICAL RECORD.</div>
      </div>`
    }).join('')
    const el = this.screen(
      'archive',
      `<h2>THE ARCHIVE</h2>
      <div class="record-count dim">${owned.size} / ${ARCHIVE_FRAGMENTS.length} RECORDS RECOVERED · RECORDS FILE IN ORDER REGARDLESS OF WHERE THEY ARE FOUND</div>
      ${quietEndingSeen ? '<div class="frag-note hi">QUIET SIGN-OFF ON FILE — THE PARTICIPANT KNOWS WHAT THE SHOW IS</div>' : ''}
      <div class="frag-list">${rows}</div>
      <button data-a="back">RETURN</button>`
    )
    this.wireButtons(el)
    el.querySelector('[data-a="back"]')!.addEventListener('click', () => {
      this.cb.uiSound('select')
      this.cb.quitToMenu()
    })
  }

  showPause(amenities: string[] = []): void {
    const el = this.screen(
      'pause',
      `
      <h2>BROADCAST SUSPENDED</h2>
      <div class="dim">the audience has been shown a placeholder</div>
      ${amenities.length ? `<div class="dim">AMENITIES IN EFFECT · ${amenities.join(' · ')}</div>` : ''}
      <nav class="menu-nav">
        <button data-a="resume">RESUME PARTICIPATION</button>
        <button data-a="settings">COMPLIANCE SETTINGS</button>
        <button data-a="quit">ABANDON RUN</button>
      </nav>`
    )
    this.wireButtons(el)
    el.querySelector('[data-a="resume"]')!.addEventListener('click', () => this.cb.resume())
    el.querySelector('[data-a="settings"]')!.addEventListener('click', () => this.showSettings(() => this.showPause(amenities)))
    el.querySelector('[data-a="quit"]')!.addEventListener('click', () => this.cb.quitToMenu())
    // Esc closes the pause menu it opened — pressing it twice never re-pauses
    this.setEscAction(() => this.cb.resume())
  }

  private statsRows(stats: RunStats): string {
    const t = Math.max(0, Math.round(stats.runTime))
    const clock = `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`
    return `
        <div>ASSETS PROCESSED <b>${stats.kills}</b></div>
        <div>RATIONS CONSUMED <b>${stats.foodEaten}</b></div>
        <div>TIME ON AIR <b>${clock}</b></div>`
  }

  showGameOver(
    score: number,
    floor: number,
    isHigh: boolean,
    signOff: string,
    mode: GameMode,
    stats: RunStats,
    seed: number,
    daily?: string,
    amenities: string[] = [],
    reportPending = false
  ): void {
    const el = this.screen(
      'gameover',
      `
      <h2>PARTICIPATION CONCLUDED</h2>
      <div class="daise-signoff">“${signOff}”</div>
      <div class="end-stats">
        ${daily ? `<div class="hi">THIS CONCLUDES THE SCHEDULED DAILY BROADCAST FOR ${daily}</div>` : ''}
        <div>FINAL INDEX <b>${String(score).padStart(7, '0')}</b>${isHigh ? ' <span class="hi">— NEW RECORD, FLAGGED FOR REVIEW</span>' : ''}</div>
        <div>DEPTH REACHED <b>FLOOR ${floor}</b></div>${this.statsRows(stats)}
        ${stats.deathCause ? `<div>CAUSE OF EXPIRATION <b>${stats.deathCause.toUpperCase()}</b></div>` : ''}
        ${amenities.length ? `<div class="dim">AMENITIES HELD · ${amenities.join(' · ')}</div>` : ''}
        <div class="seed-line dim">BROADCAST SEED ${seedLabel(seed)}</div>
        ${incidentBlock(reportPending)}
      </div>
      <nav class="menu-nav">
        <button data-a="retry">RE-ENTER THE NETWORK</button>
        <button data-a="menu">RETURN TO INTAKE</button>
      </nav>`
    )
    this.wireButtons(el)
    // retry restarts the run that just ended: the daily re-airs, otherwise the mode restarts
    el.querySelector('[data-a="retry"]')!.addEventListener('click', () => {
      if (daily) this.cb.startDaily()
      else if (mode === 'endless') this.cb.startEndless()
      else this.cb.startCampaign()
    })
    el.querySelector('[data-a="menu"]')!.addEventListener('click', () => this.cb.quitToMenu())
  }

  showWin(score: number, stats: RunStats, seed: number, ending: 'renewal' | 'archive', amenities: string[] = [], reportPending = false): void {
    const endingLine =
      ending === 'archive'
        ? 'ENDING LOGGED — FULL DISCLOSURE · QUIET SIGN-OFF ON FILE'
        : 'ENDING LOGGED — RENEWAL · THE BROADCAST DOES NOT END, IT RENEWS'
    const el = this.screen(
      'win',
      `
      <h2 class="win-title">FULL DESCENT COMPLETE</h2>
      <div class="daise-signoff">“You have exhausted the scheduled programming. The network has not exhausted you. Overtime is available immediately.”</div>
      <div class="end-stats">
        <div>INDEX AT SUMMIT <b>${String(score).padStart(7, '0')}</b></div>${this.statsRows(stats)}
        <div class="dim">${endingLine}</div>
        ${amenities.length ? `<div class="dim">AMENITIES HELD · ${amenities.join(' · ')}</div>` : ''}
        <div class="hi">OVERTIME PROGRAMMING UNSEALED — THE RUN MAY CONTINUE</div>
        <div class="seed-line dim">BROADCAST SEED ${seedLabel(seed)}</div>
        ${incidentBlock(reportPending)}
      </div>
      <nav class="menu-nav">
        <button data-a="overtime">CONTINUE INTO OVERTIME</button>
        <button data-a="conclude">CONCLUDE BROADCAST</button>
      </nav>`
    )
    this.wireButtons(el)
    el.querySelector('[data-a="overtime"]')!.addEventListener('click', () => this.cb.continueOvertime())
    el.querySelector('[data-a="conclude"]')!.addEventListener('click', () => this.cb.concludeWin())
  }

  /**
   * Between-floor procurement window. At most one approval per interval;
   * the caller re-invokes with an updated offer after a purchase so the
   * panel reflects the new rank immediately. Esc declines.
   */
  showRequisition(
    offer: RequisitionOffer,
    cb: {
      purchase: (id: RequisitionId) => void
      purchaseAmenity: (id: string) => void
      investResidual: () => void
      proceed: () => void
    }
  ): void {
    const rows = offer.tracks
      .map((tr) => {
        const c = REQUISITION_COPY[tr.id]
        const pips = '▮'.repeat(tr.rank) + '░'.repeat(Math.max(0, tr.maxRanks - tr.rank))
        const maxed = tr.price === null
        const affordable = !maxed && offer.score >= tr.price!
        const label = maxed
          ? 'FULLY PROVISIONED'
          : affordable
            ? `APPROVE — ${tr.price}`
            : `INSUFFICIENT INDEX — ${tr.price}`
        const disabled = maxed || offer.bought || !affordable
        return `<div class="req-row">
          <div class="req-info">
            <div class="req-head"><span class="req-name">${c.name}</span><span class="req-pips">${pips}</span></div>
            <div class="req-desc dim">${c.desc}</div>
          </div>
          <button class="req-buy" data-req="${tr.id}" ${disabled ? 'disabled' : ''}>${label}</button>
        </div>`
      })
      .join('')
    const amenityRows = offer.amenities
      .map((a) => {
        const affordable = offer.score >= a.price
        const label = a.owned ? 'IN EFFECT' : affordable ? `APPROVE — ${a.price}` : `INSUFFICIENT INDEX — ${a.price}`
        const disabled = a.owned || offer.bought || !affordable
        return `<div class="req-row">
          <div class="req-info">
            <div class="req-head"><span class="req-name">${a.name}</span>${a.owned ? '<span class="req-pips">▮</span>' : ''}</div>
            <div class="req-desc dim">${a.desc}</div>
          </div>
          <button class="req-buy" data-amenity="${a.id}" ${disabled ? 'disabled' : ''}>${label}</button>
        </div>`
      })
      .join('')
    const residualRow = offer.nextUnlock
      ? `<div class="req-row">
          <div class="req-info">
            <div class="req-head"><span class="req-name">RESIDUALS ${offer.residuals}</span></div>
            <div class="req-desc dim">invest to permanently add ${offer.nextUnlock.name} to the amenity stock — all future runs</div>
          </div>
          <button class="req-buy" data-a="invest" ${offer.residuals < offer.nextUnlock.cost ? 'disabled' : ''}>INVEST — ${offer.nextUnlock.cost}</button>
        </div>`
      : `<div class="req-sub dim">RESIDUALS ${offer.residuals} · THE AMENITY STOCK IS FULLY PROVISIONED</div>`
    const el = this.screen(
      'requisition',
      `
      <h2>SANCTIONED REQUISITIONS</h2>
      <div class="req-sub dim">INTERSTITIAL PROCUREMENT WINDOW · ONE APPROVAL PER INTERVAL · ALL SALES ARE FINAL</div>
      <div class="prov-plate">
        <div class="prov-name">THE PROVISIONER</div>
        <div class="prov-line">“${offer.provisioner}”</div>
      </div>
      <div class="req-balance">AVAILABLE INDEX <b>${String(offer.score).padStart(7, '0')}</b>${offer.spent > 0 ? `<span class="dim"> · EXPENDITURE TO DATE ${offer.spent}</span>` : ''}</div>
      <div class="req-list">${rows}</div>
      <div class="req-sub dim">AMENITIES · RUN-SCOPED · SHARED APPROVAL LIMIT</div>
      <div class="req-list">${amenityRows}</div>
      ${residualRow}
      ${offer.bought ? '<div class="req-note hi">APPROVAL GRANTED. THE WINDOW IS CLOSED UNTIL THE NEXT INTERVAL.</div>' : ''}
      <button data-a="proceed">${offer.bought ? `PROCEED TO FLOOR ${String(offer.nextFloor).padStart(2, '0')} — EXPENDITURE LOGGED` : `DECLINE — PROCEED TO FLOOR ${String(offer.nextFloor).padStart(2, '0')}`}</button>`
    )
    document.body.classList.add('req-open')
    this.wireButtons(el)
    el.querySelectorAll<HTMLButtonElement>('button[data-req]').forEach((b) => {
      b.addEventListener('click', () => {
        this.cb.uiSound('select')
        cb.purchase(b.dataset.req as RequisitionId)
      })
    })
    el.querySelectorAll<HTMLButtonElement>('button[data-amenity]').forEach((b) => {
      b.addEventListener('click', () => {
        this.cb.uiSound('select')
        cb.purchaseAmenity(b.dataset.amenity!)
      })
    })
    el.querySelector<HTMLButtonElement>('[data-a="invest"]')?.addEventListener('click', () => {
      this.cb.uiSound('select')
      cb.investResidual()
    })
    const proceedBtn = el.querySelector<HTMLButtonElement>('[data-a="proceed"]')!
    proceedBtn.addEventListener('click', () => {
      this.cb.uiSound('select')
      cb.proceed()
    })
    // focus the first actionable choice; after a purchase that is proceeding
    const firstBuy = el.querySelector<HTMLButtonElement>('button[data-req]:not([disabled])')
    ;(offer.bought ? proceedBtn : (firstBuy ?? proceedBtn)).focus()
    this.setEscAction(() => cb.proceed())
  }

  /**
   * transient interstitial between floors; `bonuses` is the previous floor's
   * clear settlement, `directive` the overtime directive in force (endless)
   */
  floorCard(title: string, sub: string, bonuses = '', directive = ''): void {
    const card = document.createElement('div')
    card.className = 'floor-card'
    card.innerHTML = `<div class="fc-title">${title}</div>${directive ? `<div class="fc-directive">${directive}</div>` : ''}<div class="fc-sub">${sub}</div>${bonuses ? `<div class="fc-bonus">${bonuses}</div>` : ''}`
    document.getElementById('app')!.appendChild(card)
    window.setTimeout(() => card.classList.add('fade'), 1500)
    window.setTimeout(() => card.remove(), 2100)
  }
}
